import { reduceRight, sample, sortBy } from 'lodash'
import PropTypes from 'prop-types'
import { PureComponent } from 'react'
import { connect } from 'react-redux'
import { Divider, Dropdown, Icon } from 'semantic-ui-react'

import { trackEvent } from 'global-utils/Analytics'
import { IMAGE_SIZES, Trophy } from 'images'
import { BASE_URL as searchURL } from 'search'
import System from 'system'
import { ProgressBar } from 'ui'

import ImageAdHolder from '../../site/ImageAdHolder'
import SoccerTournament from '../soccer/Tournament'
import { tournamentLastViewed } from '../soccer/TournamentActions'
import { tournamentListFetch, tournamentListFetchCanceled } from './TournamentListActions'

import './TournamentList.css'

class TournamentList extends PureComponent {
  componentDidMount() {
    const { auth, owner, tournaments, type } = this.props

    const ownerId = owner.id
    const userId = ownerId && ownerId !== auth?.usr?.id ? ownerId : null

    if (tournaments.type !== type || tournaments.id !== userId) {
      this.props.tournamentListFetch(userId, 1, type)
    }
  }

  componentDidUpdate() {
    const { lastViewed, tournaments } = this.props
    if (!lastViewed || !this.isLastViewedListed()) {
      const newTournament = sample(tournaments.data)
      if (newTournament) {
        this.props.tournamentLastViewed(newTournament.id)
      }
    }
  }

  componentWillUnmount() {
    this.props.tournamentListFetchCanceled()
  }

  getTournamentSelector = () => {
    const {
      lastViewed,
      tournaments: { data },
    } = this.props
    const totalInstitutions = Object.keys(
      reduceRight(data, (flattened, tournament) => ({ ...flattened, [tournament.institution.id]: true }), {}),
    ).length

    const getName = (tournament) => {
      const { name: tName } = tournament
      let {
        institution: { name: iName, nickname: iNick },
      } = tournament
      if (
        tName
          .replaceAll(/[\W+]/g, '')
          .toLocaleLowerCase()
          .includes(iName.replaceAll(/[\W+]/g, '').toLocaleLowerCase()) ||
        tName.replaceAll(/[\W+]/g, '').toLocaleLowerCase().includes(iNick.replaceAll(/[\W+]/g, '').toLocaleLowerCase())
      ) {
        iName = ''
        iNick = ''
      }

      return {
        key: [iNick || iName, tName, tournament.season, tournament.id].join(''),
        text: (
          <div className="flex items-center gap-2 overflow-hidden w-full text-text">
            <Trophy
              className="shrink border-current text-current"
              size="2em"
              src={tournament.avatar}
              srcImageSize={IMAGE_SIZES.FIT_100_100}
            />
            <div className="grow flex flex-col overflow-hidden">
              <div className="truncate">
                {totalInstitutions > 1 && !!(iNick || iName) && <>{iNick || iName} - </>}
                {tName}
              </div>
              <div className="text-sm opacity-80">
                {tournament.season} - {tournament.discipline.name}
              </div>
            </div>
            <Icon className="shrink self-start text-grey" name={tournament.discipline.sport.icon || 'soccer'} />
          </div>
        ),
      }
    }

    const options = data.map((elem) => ({
      value: elem.id,
      ...getName(elem),
    }))

    return (
      <Dropdown
        className="TournamentList__dropdown"
        fluid
        onChange={this.onTournamentChange}
        options={sortBy(options, ['key'])}
        placeholder="Torneo"
        selection
        value={lastViewed}
      />
    )
  }

  isLastViewedListed = () =>
    !!this.props.lastViewed && this.props.tournaments?.data?.some(({ id }) => id === this.props.lastViewed)

  onTournamentChange = (_event, { value }) => {
    const tournament = this.props.tournaments.data.find(({ id }) => id === value)
    if (tournament) {
      this.props.tournamentLastViewed(value)
      trackEvent('Component', 'Select', `Tournament - ${tournament.name}`)
    }
  }

  renderEmptyList() {
    const {
      auth: { usr },
      owner,
      tournaments: { data },
    } = this.props

    if (data && data.length) {
      return null
    }

    return (
      <div style={{ clear: 'both', textAlign: 'center' }}>
        <Divider fitted />
        <sup>
          <Icon name="quote left" size="small" />
        </sup>
        No hay torneos activos.
        <sup>
          <Icon name="quote right" size="small" />
        </sup>
        {!!usr && !!owner && usr.id === owner.id && (
          <div>
            <br />
            Haz clic en el botón&nbsp;
            <Icon
              circular
              color="green"
              inverted
              name="search"
              onClick={() => System.history.push(searchURL)}
              size="small"
            />
            &nbsp;para seguir torneos.
          </div>
        )}
      </div>
    )
  }

  render() {
    const { fixedTab, highlightedTeams, lastViewed, tab, tournaments, widget } = this.props

    return (
      <div className="TournamentList">
        <div className="p-2 bg-white rounded">
          {tournaments.loading ? (
            <ProgressBar value={100} />
          ) : (
            this.renderEmptyList() || (
              <>
                {this.getTournamentSelector()}
                {this.isLastViewedListed() && (
                  <SoccerTournament
                    key={lastViewed}
                    fixedTab={fixedTab}
                    highlightedTeams={highlightedTeams}
                    tab={tab}
                    tournamentID={lastViewed}
                    widget={widget}
                  />
                )}
              </>
            )
          )}
        </div>
        <div className="flex items-center justify-center">
          <div className="p-2 bg-white rounded border mt-2">
            <ImageAdHolder className="rounded block" name="Patrocinio Lista de Torneos" type="550" />
          </div>
        </div>
      </div>
    )
  }
}

TournamentList.propTypes = {
  auth: PropTypes.object.isRequired,
  owner: PropTypes.object.isRequired,
  tournamentLastViewed: PropTypes.func.isRequired,
  tournamentListFetch: PropTypes.func.isRequired,
  tournamentListFetchCanceled: PropTypes.func.isRequired,
  tournaments: PropTypes.object.isRequired,

  fixedTab: PropTypes.bool,
  highlightedTeams: PropTypes.array,
  lastViewed: PropTypes.string,
  tab: PropTypes.oneOf(['calendar', 'positions']),
  type: PropTypes.string,
  widget: PropTypes.bool,
}

TournamentList.defaultProps = {
  fixedTab: false,
  highlightedTeams: [],
  lastViewed: null,
  tab: 'calendar',
  type: 'user',
  widget: false,
}

export default connect(
  ({ auth, tournament: { lastViewed }, tournaments }) => ({
    auth,
    lastViewed,
    tournaments,
  }),
  {
    tournamentLastViewed,
    tournamentListFetch,
    tournamentListFetchCanceled,
  },
)(TournamentList)
